/**
 * GOOP ZONE — Shrine System
 * ========================
 * Design Philosophy: Industrial dungeon aesthetic, dark grunge.
 * 
 * GOOP Shrines are salvage crate altars scattered randomly across the map.
 * The player must stand within the activation radius and channel (stay close)
 * for CHANNEL_DURATION_MS to activate the shrine and receive a random buff.
 * 
 * Shrine Design: Stone circle on the dungeon floor with a peanut face carved
 * in the center. Same sprite set used on ALL maps (no per-level variants).
 * 
 * Mechanic:
 *   - IDLE:       Stone circle, peanut face, NO glow. Player is NOT on it.
 *   - CHANNELING: Player STEPS ON the circle → eyes glow cyan, rune rings light
 *                 up, progress bar fills. If player steps OFF → full reset to idle.
 *   - SPENT_LIT:  Channeling completes → eyes stay glowing permanently for the
 *                 rest of the level. Buff granted. Glow never turns off.
 * 
 * Interaction triggers (main game engine responsibility):
 *   - Chests:      E (keyboard) or X (controller) when in CHEST_INTERACT_RADIUS
 *   - Boss portal: E (keyboard) or X (controller) when overlapping portal tile
 *   - Shrines:     Player must physically STAND ON the circle (proximity only,
 *                  no button press). Stepping off resets progress to zero.
 * 
 * Integration note for main game engine:
 *   - Call updateShrines(shrines, playerPos, deltaMs) every frame
 *   - When a shrine returns state === 'activated', show buff selection UI
 *   - After buff is chosen, call markShrineSpent(shrine)
 *   - Spent shrines render with SHRINE_ASSETS.spentLit (permanently glowing)
 */

// BETA 16.13 — Per Dylan: "shrine auto does do start until it is full
// activated" — the shrine blessing sound should only play when the
// shrine is fully activated, not on step-on / channel-start. Removed
// startShrineActivate (channel-start streaming sound), now using
// playShrineActivate (one-shot at full activation).
import { playShrineActivate } from './gameSfx';
// BETA 16.12 — Replaced pauseBgm with startMuffledDuck per Dylan: "main
// game audio playing in game ducks heavy and sounds like you put a
// blanked over the speaker. still playin and not stopped or paused.
// just under." Music keeps playing during shrine UI but at 40% volume
// + lowpass filter (sounds muffled).
import { startMuffledDuck } from './audio';
import {
  BORDER_COLLISION_NORTH, BORDER_COLLISION_SOUTH,
  BORDER_COLLISION_WEST,  BORDER_COLLISION_EAST,
} from './borderWalls';

// Tracks active channeling stop functions keyed by shrine ID
const channelingStops = new Map<string, () => void>();

// ─── Asset URLs ──────────────────────────────────────────────────────────────

// BETA 22.94 — TWO platform states (Dylan: "one is not activated one is
// activated… when it is activated it needs to stay illuminated").
// platform.webp = Dylan's original LIT art (blue neon ring on).
// platform_idle.webp = the same file with ONLY the blue neon pixels dimmed to
// an unlit dark tube (hue-targeted, stones untouched, identical 512×457 canvas
// → the two states overlay pixel-perfect for the ignition crossfade).
export const SHRINE_ASSETS = {
  idle:      '/shrine-tiles/platform_idle.webp',
  active:    '/shrine-tiles/platform_idle.webp',
  activated: '/shrine-tiles/platform.webp',
  spentLit:  '/shrine-tiles/platform.webp',
  mapIcon:   '/shrine-tiles/platform.webp',
} as const;

// 23.39 — BOSS_SHRINE_ASSETS deleted with the boss shrine (CaseOh purge).

// ─── Constants ────────────────────────────────────────────────────────────────

/** World-unit radius the player must stay within to channel the shrine */
export const SHRINE_ACTIVATION_RADIUS = 100; // tight to shrine border (half of SHRINE_SPRITE_SIZE)

/** Time in milliseconds to fully channel the shrine */
export const SHRINE_CHANNEL_DURATION_MS = 3500;

/** Visual size of the shrine sprite in world units (square) */
export const SHRINE_SPRITE_SIZE = 200;

/**
 * Number of shrines placed per map.
 * With 16 clusters (~64 rooms) → ~128 chests, so target is ~half = 50 shrines.
 */
// BETA 20.81 — back to the ORIGINAL 50 (Dylan: still not enough at 25).
// The 20.77 cut (50→14) was a god-mode reaction, but god mode is fixed at
// the SOURCE now (regen gate / lifesteal cap / meat taper) — shrine COUNT
// isn't the lever for it. Dylan likes a shrine-rich map; each shrine is also
// luck-scaled + de-duped now, so quality (not scarcity) handles staleness.
export const SHRINE_COUNT_PER_MAP = 16;   // 23.24 — Dylan: shrines "should be more rare" (was 50)

/**
 * Minimum world-unit distance between any two shrines.
 * Was 1500 — room exclusion zones (768/2 + 400 = 784 each side) were eating
 * the entire map, leaving almost no valid positions → only ~8 shrines placed.
 */
export const SHRINE_MIN_SPACING = 700;

/** Minimum world-unit distance from the player spawn point */
export const SHRINE_MIN_SPAWN_DISTANCE = 600;

/** Boss shrine constants */
export const BOSS_SHRINE_SPRITE_SIZE        = 260;   // slightly larger than buff shrine
export const BOSS_SHRINE_ACTIVATION_RADIUS  = 130;
export const BOSS_SHRINE_CHANNEL_DURATION_MS = 2000; // 2s to summon — shorter, more dangerous
export const BOSS_SHRINE_COUNT_PER_MAP      = 3;
export const BOSS_SHRINE_MIN_SPACING        = 2500;  // far apart — finding one is special
export const BOSS_SHRINE_MIN_SPAWN_DISTANCE = 1200;  // well away from player start

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * idle          — waiting, player not nearby
 * channeling    — player in radius, progress bar filling
 * activated     — just reached 100% — show buff/spawn UI this frame
 * spent_lit     — buff chosen, shrine permanently glowing for rest of level
 * boss_spawned  — (boss shrine only) boss is alive
 * boss_defeated — (boss shrine only) boss killed, shrine dormant
 */
export type ShrineState =
  | 'idle' | 'channeling' | 'activated' | 'spent_lit'
  | 'boss_spawned' | 'boss_defeated';

/**
 * A single shrine instance on the map.
 * Stored in RoomMap.shrines[].
 */
export interface Shrine {
  /** Unique ID for this shrine instance */
  id: string;
  /** World-space center position */
  x: number;
  y: number;
  /** Current activation state */
  state: ShrineState;
  /**
   * Channel progress in [0, 1].
   * RESETS to 0 if the player leaves the activation radius before completing.
   */
  progress: number;
  /** Accumulated channel time in ms (used to compute progress) */
  channelMs: number;
  /** Golden shrines offer all-Legendary buff cards */
  golden?: boolean;
  /** 'buff' (default) or 'boss' — determines what happens on activation */
  type?: 'buff' | 'boss';
  /** Enemy definitionId to spawn when a boss shrine activates */
  bossEnemyId?: string;
  /** Runtime enemy ID of the spawned boss (set by engine after spawn) */
  bossInstanceId?: number;
  /** BETA 22.94 — wall-clock ms when the lit-state ignition (flicker-on)
   *  began. Undefined = shrine was never claimed (or pre-22.94 save state:
   *  renders fully lit, no animation). The engine re-bases this while the
   *  choice screen is up so the player SEES the light strike on. */
  litAtMs?: number;
  /** BETA 22.94 — one-shot guard so the ignite SFX plays exactly once. */
  igniteSfxPlayed?: boolean;
}

/**
 * A buff that can be awarded when a shrine is activated.
 * The actual buff pool comes from the Items & GOOP module.
 * This interface is a placeholder — the main game engine will
 * populate SHRINE_BUFF_POOL from the items module at runtime.
 */
export interface ShrineBuff {
  id: string;
  label: string;
  description: string;
  /** Icon URL (from items module CDN) */
  iconUrl: string;
  /** The effect applied to the player when chosen */
  apply: (playerState: unknown) => void;
}

// ─── Placement ────────────────────────────────────────────────────────────────

/**
 * Generates shrine positions for a map.
 * Called by generateRoomMap() — shrines are placed in the open ground
 * between clusters, never inside a room footprint.
 *
 * @param seed        Seeded RNG value (use map seed + offset)
 * @param worldSize   World size in units (from WORLD_SIZE constant)
 * @param rooms       Array of placed rooms (to avoid overlap)
 * @param spawnPoint  Player spawn position (shrines must be far enough away)
 * @returns           Array of Shrine objects with state='idle', progress=0
 */
export function generateShrines(
  seed: number,
  worldSize: number,
  rooms: Array<{ x: number; y: number; size: number }>,
  spawnPoint: { x: number; y: number }
): Shrine[] {
  const shrines: Shrine[] = [];

  // Spawn bounds = inside the actual border walls, not a % guess.
  // 120wu inner padding so shrines don't sit right at the collision line.
  const PAD = 120;
  const xMin = BORDER_COLLISION_WEST + PAD;
  const xMax = worldSize - BORDER_COLLISION_EAST - PAD;
  const yMin = BORDER_COLLISION_NORTH + PAD;
  const yMax = worldSize - BORDER_COLLISION_SOUTH - PAD;

  // Simple seeded LCG RNG
  let s = seed ^ 0xDEADBEEF;
  const rng = () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xFFFFFFFF;
  };

  let attempts = 0;
  while (shrines.length < SHRINE_COUNT_PER_MAP && attempts < 25000) {
    attempts++;
    const cx = xMin + rng() * (xMax - xMin);
    const cy = yMin + rng() * (yMax - yMin);

    // Must not be inside any room footprint (compare against room CENTER, not top-left)
    const inRoom = rooms.some(r => {
      const roomCenterX = r.x + r.size / 2;
      const roomCenterY = r.y + r.size / 2;
      // Was 400 extra buffer — combined with 96 rooms × 768 size, this consumed the
      // entire map and left almost nowhere for shrines to place. Reduced to 120.
      const half = r.size / 2 + 120;
      return Math.abs(cx - roomCenterX) < half && Math.abs(cy - roomCenterY) < half;
    });
    if (inRoom) continue;

    // Must be far enough from spawn
    const dSpawn = Math.hypot(cx - spawnPoint.x, cy - spawnPoint.y);
    if (dSpawn < SHRINE_MIN_SPAWN_DISTANCE) continue;

    // Must be far enough from other shrines
    const tooClose = shrines.some(sh =>
      Math.hypot(cx - sh.x, cy - sh.y) < SHRINE_MIN_SPACING
    );
    if (tooClose) continue;

    shrines.push({
      id: `shrine_${shrines.length}_${seed}`,
      x: cx,
      y: cy,
      state: 'idle',
      progress: 0,
      channelMs: 0,
      golden: rng() < 0.15,
    });
  }

  return shrines;
}

/**
 * 23.39 — BOSS SHRINES DELETED (the CaseOh boss and his shrine are fully
 * removed from the game on Dylan's order; original bosses arrive with the
 * Phase C enemy roster redo). TOMBSTONE: keeps the export so callers and
 * PSBOT-owned files compile; always returns an empty list — no boss shrine
 * ever generates.
 */
export function generateBossShrines(
  _seed: number,
  _worldSize: number,
  _rooms: Array<{ x: number; y: number; size: number }>,
  _spawnPoint: { x: number; y: number },
  _existingShrines: Shrine[],
): Shrine[] {
  return [];
}

// ─── Runtime Update ───────────────────────────────────────────────────────────

/**
 * Updates all shrine states based on player proximity.
 * Call this every game frame from the main engine update loop.
 *
 * @param shrines    The shrine array from RoomMap (mutated in place)
 * @param playerPos  Current player world position
 * @param deltaMs    Milliseconds elapsed since last frame
 * @returns          Array of shrine IDs that just reached 'activated' this frame
 */
export function updateShrines(
  shrines: Shrine[],
  px: number,
  py: number,
  deltaMs: number
): string[] {
  const justActivated: string[] = [];

  for (const shrine of shrines) {
    // Boss shrines use E-key instant activation — skip channeling logic
    if (shrine.type === 'boss') continue;
    // Skip permanently completed shrines and those awaiting buff selection
    if (shrine.state === 'spent_lit' || shrine.state === 'activated') continue;

    // BETA 14.64 — squared-distance compare avoids the Math.hypot sqrt.
    const dx = px - shrine.x;
    const dy = py - shrine.y;
    const inRadius = (dx * dx + dy * dy) <= (SHRINE_ACTIVATION_RADIUS * SHRINE_ACTIVATION_RADIUS);

    const prevState = shrine.state;

    if (inRadius) {
      shrine.state = 'channeling';
      shrine.channelMs += deltaMs;
      const channelDuration = SHRINE_CHANNEL_DURATION_MS;
      shrine.progress = Math.min(shrine.channelMs / channelDuration, 1);

      // BETA 16.13 — Per Dylan: "shrine auto does do start until it is
      // full activated." The shrine blessing sound now ONLY plays at
      // full activation (block below), NOT on channel-start. Was: a
      // long-buffer streaming sound triggered at first channel tick that
      // bridged through to activation. Now: silent during channeling,
      // one-shot blessing sound at the moment of full unlock.
      if (prevState !== 'channeling') {
        // No sound here anymore — channel-up is silent.
      }

      if (shrine.progress >= 1) {
        shrine.state = 'activated';
        channelingStops.delete(shrine.id);
        // BETA 16.13 — Play the shrine blessing sound HERE (full activation),
        // not at channel-start.
        playShrineActivate();
        // BETA 16.12 — Shrine fully unlocked: muffled-duck the BGM (heavy
        // volume drop + lowpass) instead of pausing. Per Dylan: "still
        // playin and not stopped or paused. just under." endMuffledDuck
        // fires later from itemsAdapter.handleShrineSelection (buff picked)
        // or skipShrineChoice (player skipped).
        startMuffledDuck();
        justActivated.push(shrine.id);
      }
    } else {
      // Player LEFT the radius — FULL RESET. Stop the sound mid-play and
      // reset channel progress. If they step back, startShrineActivate()
      // fires fresh from the beginning of the file.
      if (prevState === 'channeling') {
        const stop = channelingStops.get(shrine.id);
        if (stop) { stop(); channelingStops.delete(shrine.id); }
      }
      shrine.state = 'idle';
      shrine.channelMs = 0;
      shrine.progress = 0;
    }
  }

  return justActivated;
}

/**
 * Marks a shrine as spent after the player has chosen their buff.
 * Call this from the buff selection UI after a buff is chosen.
 */
/**
 * Marks a shrine as permanently lit after the player has chosen their buff.
 * Call this from the buff selection UI after a buff is chosen.
 * The shrine will render with the blazing cyan energy pillar sprite
 * (SHRINE_ASSETS.spentLit) for the rest of the level.
 */
export function markShrineSpent(shrine: Shrine): void {
  shrine.state = 'spent_lit';
  shrine.progress = 1;
  // BETA 22.94 — start the light-switch ignition clock. The engine holds
  // this at "now" while the choice screen covers the world, so the flicker
  // plays right when gameplay resumes and the player is looking.
  shrine.litAtMs = performance.now();
  shrine.igniteSfxPlayed = false;
}

// ─── Rendering Helpers ────────────────────────────────────────────────────────

/**
 * Returns the correct sprite URL for a shrine based on its current state.
 * Use this in the canvas renderer to pick which image to draw.
 */
export function getShrineSprite(shrine: Shrine): string {
  // 23.39 — boss shrines deleted (CaseOh purge); every shrine is a buff shrine
  if (shrine.state === 'spent_lit') return SHRINE_ASSETS.spentLit;
  return SHRINE_ASSETS.idle;
}

/**
 * Draws the shrine channeling progress arc on a canvas context.
 * Call this AFTER drawing the shrine sprite, in the game render loop.
 *
 * @param ctx       Canvas 2D rendering context
 * @param sx        Screen X of shrine center
 * @param sy        Screen Y of shrine center
 * @param radiusX   Horizontal radius in pixels (drawn platform half-width)
 * @param radiusY   Vertical radius in pixels (drawn platform half-height)
 * @param progress  Progress value in [0, 1]
 */
// BETA 22.93 — ELLIPSE, not circle: the platform sprite draws aspect-fit
// (wider than tall), so a circular arc floated off the art's top/bottom
// edge. Callers pass the drawn footprint's half-extents.
export function drawShrineProgressArc(
  ctx: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  radiusX: number,
  radiusY: number,
  progress: number,
  color = '#00ff88',
): void {
  if (progress <= 0) return;

  const startAngle = -Math.PI / 2;
  const endAngle = startAngle + Math.PI * 2 * progress;
  const brightColor = color === '#ffd700' ? '#ffe566' : '#aaffcc';

  ctx.save();
  // BETA 20.77 — shadowBlur removed (13.70 perf law; Dylan's run was
  // render-bound with 41 shrine activations). The bright inner stroke below
  // already provides the glow read.
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.ellipse(sx, sy, radiusX, radiusY, 0, startAngle, endAngle);
  ctx.stroke();

  ctx.strokeStyle = brightColor;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(sx, sy, radiusX, radiusY, 0, startAngle, endAngle);
  ctx.stroke();
  ctx.restore();
}

/**
 * BETA 22.94 — SEGMENTED BLUE charge meter along the INSIDE edge of the
 * platform (Dylan's concept art: blue blocks filling around the ring while
 * you stand still to claim). Replaces the old green arc + green dashed hint
 * entirely — the shrine's color is BLUE, full stop.
 *
 * Draws directly over the unlit neon channel of platform_idle.webp:
 * (rx, ry) are the platform's drawn half-extents; the segment track runs at
 * ~86% of them, which is where the neon tube sits in the art.
 * No shadowBlur (13.70 perf law) — glow comes from layered strokes.
 *
 * @param timeMs  performance.now() — drives the leading-segment pulse/sparks
 */
export function drawShrineChargeSegments(
  ctx: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  rx: number,
  ry: number,
  progress: number,
  timeMs: number,
): void {
  const SEGS = 22;
  const INSET = 0.86;                       // neon tube position in the art
  const srx = rx * INSET, sry = ry * INSET;
  const span = (Math.PI * 2) / SEGS;
  const gap = span * 0.22;                  // air between blocks
  const start = -Math.PI / 2;               // fill clockwise from the top
  const filled = Math.floor(progress * SEGS);
  const lead = Math.min(filled, SEGS - 1);  // the segment currently charging

  ctx.save();
  ctx.lineCap = 'butt';

  for (let i = 0; i < SEGS; i++) {
    const a0 = start + i * span + gap / 2;
    const a1 = start + (i + 1) * span - gap / 2;
    const isFilled = i < filled;
    const isLead = i === lead && progress < 1;

    if (!isFilled && !isLead) {
      // dark track — the unlit blocks ahead
      ctx.strokeStyle = 'rgba(40, 70, 110, 0.45)';
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.ellipse(sx, sy, srx, sry, 0, a0, a1);
      ctx.stroke();
      continue;
    }

    // soft outer glow layer (wide, translucent)
    const pulse = isLead ? 0.55 + 0.45 * Math.sin(timeMs / 90) : 1;
    ctx.strokeStyle = `rgba(80, 180, 255, ${0.30 * pulse})`;
    ctx.lineWidth = 11;
    ctx.beginPath();
    ctx.ellipse(sx, sy, srx, sry, 0, a0, a1);
    ctx.stroke();

    // bright core
    ctx.strokeStyle = isLead ? `rgba(225, 245, 255, ${0.55 + 0.45 * pulse})` : '#5fc9ff';
    ctx.lineWidth = isLead ? 6 : 5;
    ctx.beginPath();
    ctx.ellipse(sx, sy, srx, sry, 0, a0, a1);
    ctx.stroke();
  }

  // energy sparks crackling off the leading edge
  if (progress > 0 && progress < 1) {
    const la = start + (lead + 0.5) * span;
    const lx = sx + srx * Math.cos(la);
    const ly = sy + sry * Math.sin(la);
    ctx.globalCompositeOperation = 'lighter';
    for (let s = 0; s < 3; s++) {
      const j = (Math.sin(timeMs / 47 + s * 2.6) + Math.sin(timeMs / 31 + s * 4.1)) * 4;
      const jy = (Math.cos(timeMs / 53 + s * 3.3)) * 3;
      ctx.fillStyle = `rgba(170, 225, 255, ${0.35 + 0.3 * Math.sin(timeMs / 60 + s)})`;
      ctx.beginPath();
      ctx.arc(lx + j, ly + jy, 1.6 + s * 0.7, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  ctx.restore();
}
